import React, { useEffect, useState } from 'react';
import { ArrowRight, Play, Zap, Workflow, Server } from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../data/mock';

export const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Moving gradient orbs */}
        <div 
          className="absolute w-96 h-96 rounded-full opacity-20 animate-float"
          style={{
            background: `radial-gradient(circle, var(--accent-primary), transparent)`,
            left: `${20 + mousePosition.x * 0.02}%`,
            top: `${10 + mousePosition.y * 0.02}%`,
            animationDelay: '0s'
          }}
        ></div>
        <div 
          className="absolute w-64 h-64 rounded-full opacity-15 animate-float"
          style={{
            background: `radial-gradient(circle, var(--accent-purple), transparent)`,
            right: `${15 + mousePosition.x * 0.015}%`,
            bottom: `${20 + mousePosition.y * 0.015}%`,
            animationDelay: '2s'
          }}
        ></div>
        <div 
          className="absolute w-48 h-48 rounded-full opacity-25 animate-float"
          style={{
            background: `radial-gradient(circle, var(--accent-primary), transparent)`,
            left: `${70 + mousePosition.x * 0.01}%`,
            top: `${60 + mousePosition.y * 0.01}%`,
            animationDelay: '4s'
          }}
        ></div>

        {/* Floating Icons */}
        <div className="absolute top-20 left-20 animate-float" style={{ animationDelay: '1s' }}>
          <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)'
          }}>
            <Zap className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
          </div>
        </div>
        <div className="absolute top-40 right-32 animate-float" style={{ animationDelay: '3s' }}>
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)'
          }}>
            <Workflow className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
          </div>
        </div>
        <div className="absolute bottom-32 left-32 animate-float" style={{ animationDelay: '2s' }}>
          <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)'
          }}>
            <Server className="w-7 h-7" style={{ color: 'var(--accent-primary)' }} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center">
          {/* Hero Badge */}
          <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-8 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`} style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-primary)'
          }}>
            <Zap className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <span className="body-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
              Trusted by 500+ Companies Worldwide
            </span>
          </div>

          {/* Main Headline */}
          <h1 className={`display-lg mb-6 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`} style={{ color: 'var(--text-primary)' }}>
            {heroData.title}
          </h1>

          {/* Subtitle */}
          <p className={`body-lg max-w-3xl mx-auto mb-8 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`} style={{ color: 'var(--text-secondary)' }}>
            {heroData.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16 transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <Button 
              className="btn-primary group"
              onClick={() => scrollToSection('#contact')}
            >
              {heroData.ctaPrimary}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              className="btn-secondary group"
              onClick={() => scrollToSection('#case-studies')}
            >
              <Play className="w-5 h-5 mr-2" />
              {heroData.ctaSecondary}
            </Button>
          </div>

          {/* Stats */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-2xl mx-auto transition-all duration-1000 delay-800 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            {heroData.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="h1 mb-2" style={{ color: 'var(--accent-primary)' }}>
                  {stat.number}
                </div>
                <div className="body-md" style={{ color: 'var(--text-muted)' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 rounded-full flex justify-center" style={{
          borderColor: 'var(--text-muted)'
        }}>
          <div className="w-1 h-3 rounded-full mt-2" style={{
            background: 'var(--accent-primary)'
          }}></div>
        </div>
      </div>
    </section>
  );
};